export const assetClasses = {
  stock: "Ações",
  fii: "FIIs",
  etf: "ETFs",
  bdr: "BDRs",
  fixed: "Renda fixa",
  crypto: "Cripto",
  other: "Outros",
} as const;
export type AssetClass = keyof typeof assetClasses;
export interface InvestmentAsset {
  id: string;
  symbol: string;
  name: string;
  assetClass: AssetClass;
  price: number | null;
  quotedAt: string | null;
  quoteFetchedAt?: string;
  quoteSource: "manual" | "brapi" | null;
}
export interface InvestmentEntry {
  id: string;
  assetId: string;
  kind: "buy" | "sell" | "dividend";
  date: string;
  quantity: number;
  price: number;
  fees: number;
  notes: string;
  createdAt: string;
}
export interface Portfolio {
  assets: InvestmentAsset[];
  entries: InvestmentEntry[];
}
export interface PortfolioRecord {
  portfolio: Portfolio;
  version: number;
  quotesEnabled: boolean;
  quoteErrors?: {symbol:string;code:string;message:string}[];
}
export const emptyPortfolio = (): Portfolio => ({ assets: [], entries: [] });
export function positions(portfolio: Portfolio, today: string) {
  const entries = [...portfolio.entries].sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      a.createdAt.localeCompare(b.createdAt) ||
      a.id.localeCompare(b.id),
  );
  return portfolio.assets.map((asset) => {
    let quantity = 0,
      cost = 0,
      realized = 0,
      dividends = 0,
      provisioned = 0,
      bought = 0;
    for (const e of entries.filter((e) => e.assetId === asset.id)) {
      if (e.kind === "dividend") {
        if (e.date <= today) dividends += e.quantity * e.price;
        else provisioned += e.quantity * e.price;
        continue;
      }
      if (e.date > today) continue;
      if (e.kind === "buy") {
        quantity += e.quantity;
        cost += e.quantity * e.price + e.fees;
        bought += e.quantity * e.price + e.fees;
      } else {
        // Scale rounding tolerance to the actual holding, including tiny crypto
        // quantities. An absolute tolerance would allow sales without a position.
        const tolerance =
          Number.EPSILON * Math.max(quantity, e.quantity) * 4;
        if (quantity <= 0 || e.quantity - quantity > tolerance)
          throw new Error(
            `A venda de ${asset.symbol} em ${e.date} supera a quantidade disponível.`,
          );
        const closesPosition = Math.abs(quantity - e.quantity) <= tolerance;
        const soldQuantity = closesPosition ? quantity : e.quantity;
        const removed = closesPosition ? cost : (cost / quantity) * soldQuantity;
        realized += soldQuantity * e.price - e.fees - removed;
        quantity -= soldQuantity;
        cost -= removed;
        if (closesPosition) {
          quantity = 0;
          cost = 0;
        }
      }
    }
    const value = asset.price === null ? null : quantity * asset.price;
    return {
      asset,
      quantity,
      cost,
      average: quantity ? cost / quantity : 0,
      value,
      unrealized: value === null ? null : value - cost,
      realized,
      dividends,
      provisioned,
      bought,
    };
  });
}
export function portfolioSummary(portfolio: Portfolio, today: string) {
  const rows = positions(portfolio, today);
  const missing = rows.some((p) => p.quantity > 0 && p.value === null);
  const cost = rows.reduce((s, p) => s + p.cost, 0),
    value = rows.reduce((s, p) => s + (p.value || 0), 0);
  const realized = rows.reduce((s, p) => s + p.realized, 0),
    dividends = rows.reduce((s, p) => s + p.dividends, 0);
  return {
    rows,
    cost,
    value: missing ? null : value,
    unrealized: missing ? null : value - cost,
    realized,
    dividends,
    provisioned: rows.reduce((s, p) => s + p.provisioned, 0),
    profit: missing ? null : value - cost + realized + dividends,
  };
}
